import React, { useState } from 'react';
import { FiPhone, FiArrowLeft, FiPlay, FiPause, FiBarChart2 } from 'react-icons/fi';
import { CallLog } from '@/types/TCRMLogs';
import { parseIntakeResponses, showNotification } from '@/utils/SmartCRM/crmDetailsUtils';
import { useAudioPlayer } from 'src/contexts/AudioPlayerContext';
import { ECallRecordingType } from 'src/enums/ECallRecording';
import { useMutation } from '@tanstack/react-query';
import { getCallRecording } from '@/api/logs';

interface CallDetailViewProps {
	call: CallLog;
	onBack: () => void;
}

export const CallDetailView: React.FC<CallDetailViewProps> = ({ call, onBack }) => {
	const { playAudio, pauseAudio, isPlaying, currentRecord } = useAudioPlayer();
	const [loadingRecording, setLoadingRecording] = useState(false);
	const intakeForm = parseIntakeResponses(call.intakeResponses);

	const callRecordingMutation = useMutation<{ callSid: string; url: string }, Error, string>({
		mutationFn: getCallRecording,
		onMutate: () => {
			setLoadingRecording(true);
		},
		onSuccess: (data) => {
			setLoadingRecording(false);
			const recordData = {
				callSid: call.callSid,
			};
			playAudio(data.url, recordData, ECallRecordingType.CALL);
		},
		onError: () => {
			setLoadingRecording(false);
			showNotification('error', 'Failed to load recording', 'Unable to fetch recording file');
		},
	});

	const handlePlayRecording = () => {
		if (call.isRecorded === 'true') {
			const callSid = call.callSid;
			if (callSid) {
				callRecordingMutation.mutate(callSid);
			} else {
				showNotification('error', 'Call ID Missing', 'Unable to identify call recording');
			}
		} else {
			showNotification('warning', 'No Recording Available');
		}
	};

	const handlePauseRecording = () => {
		pauseAudio();
	};

	const isCurrentlyPlaying = isPlaying && currentRecord?.callSid === call.callSid;

	const parseCallScore = (scoreStr: string) => {
		try {
			const parsed = JSON.parse(scoreStr);
			return {
				value: parsed.value !== undefined ? parsed.value : 0,
				description: parsed.description || '',
			};
		} catch {
			return { value: 0, description: '' };
		}
	};

	const parseCallSentiments = (sentimentsStr: string) => {
		try {
			const parsed = JSON.parse(sentimentsStr);
			return {
				positive: parsed.positive || 0,
				neutral: parsed.neutral || 0,
				negative: parsed.negative || 0,
				description: parsed.description || '',
			};
		} catch {
			return { positive: 0, neutral: 0, negative: 0, description: '' };
		}
	};

	const parseTranscript = (transcript: string) => {
		if (!transcript) {
			return [];
		}

		const messages = transcript.split('\n').filter((line) => line.trim());
		return messages.map((message, index) => {
			const [speaker, ...contentParts] = message.split('@');
			const content = contentParts.join('@').trim();

			return {
				id: index,
				speaker: speaker.trim(),
				content: content || message.trim(),
				isAssistant: speaker.trim().toLowerCase() === 'assistant',
			};
		});
	};

	const getCallDirection = () => {
		if (call.from === call.to) {
			return 'Internal Call';
		}
		return call.from.includes('+1') ? 'Inbound Call' : 'Outbound Call';
	};

	const score = parseCallScore(call.callScore);
	const sentiments = parseCallSentiments(call.callSentiments);
	const transcriptMessages = parseTranscript(call.transcript || '');

	return (
		<div className="space-y-6">
			<button
				onClick={onBack}
				className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
			>
				<FiArrowLeft className="w-4 h-4" />
				Back to Call Logs
			</button>

			<div className="bg-white rounded-lg border p-6">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-4">
						<div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
							<FiPhone className="w-6 h-6 text-blue-600" />
						</div>
						<div>
							<h3 className="text-lg font-semibold">{getCallDirection()}</h3>
							<p className="text-sm text-gray-600">
								{new Date(call.startedTimestamp).toLocaleString()} • Duration:{' '}
								{Math.floor(parseInt(call.duration) / 60)}:
								{(parseInt(call.duration) % 60).toString().padStart(2, '0')}
							</p>
							<p className="text-sm text-gray-600">
								{call.from} → {call.to}
							</p>
						</div>
					</div>
					<div className="flex items-center gap-2">
						{call.isRecorded === 'true' && (
							<div className="flex items-center gap-2">
								{isCurrentlyPlaying ? (
									<button
										onClick={handlePauseRecording}
										className="flex items-center gap-1 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
									>
										<FiPause className="w-4 h-4" />
										Pause
									</button>
								) : (
									<button
										onClick={handlePlayRecording}
										disabled={loadingRecording}
										className={`flex items-center gap-1 px-3 py-2 rounded-lg transition-colors ${
											loadingRecording
												? 'bg-gray-400 cursor-not-allowed text-white'
												: 'bg-blue-600 text-white hover:bg-blue-700'
										}`}
									>
										{loadingRecording ? (
											<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
										) : (
											<FiPlay className="w-4 h-4" />
										)}
										{loadingRecording ? 'Loading...' : 'Play'}
									</button>
								)}

								{isCurrentlyPlaying && (
									<div className="flex items-center gap-1">
										<div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
										<span className="text-xs text-red-500 font-medium">NOW PLAYING</span>
									</div>
								)}
							</div>
						)}
					</div>
				</div>
			</div>

			{call.callSummary && (
				<div className="bg-white rounded-lg border p-6">
					<h4 className="text-base font-semibold mb-4">Call Summary</h4>
					<div className="bg-gray-50 p-4 rounded-lg text-sm leading-relaxed">
						{call.callSummary.replace(/"/g, '')}
					</div>
				</div>
			)}

			{call.transcript && (
				<div className="bg-white rounded-lg border p-6">
					<h4 className="text-base font-semibold mb-4">Call Transcript</h4>
					<div className="max-h-96 overflow-y-auto space-y-3 bg-gray-50 p-4 rounded-lg">
						{transcriptMessages.length > 0 ? (
							transcriptMessages.map((message) => (
								<div
									key={message.id}
									className={`flex ${message.isAssistant ? 'justify-start' : 'justify-end'}`}
								>
									<div
										className={`max-w-[70%] px-4 py-2 rounded-lg ${
											message.isAssistant
												? 'bg-blue-100 text-blue-900'
												: 'bg-green-100 text-green-900'
										}`}
									>
										<div className="text-xs font-medium mb-1 opacity-75">
											{message.isAssistant ? 'Assistant' : 'Caller'}
										</div>
										<div className="text-sm">{message.content}</div>
									</div>
								</div>
							))
						) : (
							<p className="text-sm text-gray-500 text-center">
								No transcript available for this call.
							</p>
						)}
					</div>
				</div>
			)}

			<div className="bg-white rounded-lg border p-6">
				<div className="flex items-center gap-2 mb-4">
					<FiBarChart2 className="w-5 h-5 text-blue-500" />
					<h4 className="text-base font-semibold">Call Analytics</h4>
				</div>

				<div className="grid grid-cols-2 gap-6">
					<div>
						<h5 className="font-medium mb-3">Call Score</h5>
						<div className="text-2xl font-bold text-blue-600">{score.value}/5</div>
						{score.description && <p className="text-sm text-gray-600 mt-2">{score.description}</p>}
					</div>

					<div>
						<h5 className="font-medium mb-3">Sentiment Analysis</h5>
						<div className="space-y-2">
							<div className="flex justify-between">
								<span className="text-sm">Positive</span>
								<span className="text-sm font-medium text-green-600">{sentiments.positive}%</span>
							</div>
							<div className="flex justify-between">
								<span className="text-sm">Neutral</span>
								<span className="text-sm font-medium text-gray-600">{sentiments.neutral}%</span>
							</div>
							<div className="flex justify-between">
								<span className="text-sm">Negative</span>
								<span className="text-sm font-medium text-red-600">{sentiments.negative}%</span>
							</div>
						</div>
					</div>
				</div>

				{sentiments.description && (
					<div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
						<h5 className="font-medium text-blue-900 mb-1">Sentiment Summary</h5>
						<p className="text-sm text-blue-800">{sentiments.description}</p>
					</div>
				)}
			</div>

			{intakeForm && (
				<div className="bg-white rounded-lg border p-6">
					<h4 className="text-base font-semibold mb-4">Intake Form</h4>
					<div className="space-y-4">
						{Object.entries(intakeForm).map(([question, answer], idx) => (
							<div key={idx} className="bg-gray-50 p-4 rounded-lg">
								<p className="text-sm font-medium text-gray-700">{question}</p>
								<p className="text-sm text-gray-900 mt-1">{String(answer).trim() || 'N/A'}</p>
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
};
